'use client';

import { useMemo, useState } from 'react';

import { RichText } from '@repo/ui/rich-text';

import { AboutPageMember } from 'types/sanity.types';

import Image from 'components/nextjs-fixes/image';
import World from 'components/world';

type TProps = {
  categories: {
    _key: string;
    title?: string;
    members?: AboutPageMember[];
  }[];
};

const toMapCodes = (countryCodes?: string[]) =>
  (countryCodes ?? [])
    .map((code) => code.toLowerCase().trim())
    .filter((code) => /^[a-z]{2}$/.test(code));

const MembersSection = (props: TProps) => {
  const { categories } = props;

  const [hoveredCodes, setHoveredCodes] = useState<string[]>([]);

  const allCodes = useMemo(
    () =>
      Array.from(
        new Set(
          categories.flatMap((category) =>
            (category.members ?? []).flatMap((member) =>
              toMapCodes(member.countryCodes),
            ),
          ),
        ),
      ),
    [categories],
  );

  const isHovering = hoveredCodes.length > 0;
  const activeCodes = isHovering ? hoveredCodes : allCodes;
  const activeColor = isHovering ? '#ef4444' : '#fca5a5';

  return (
    <div className="flex flex-col gap-y-16 lg:gap-y-24">
      {categories.map((category) => (
        <section key={category._key} className="flex flex-col gap-y-8">
          {category.title !== undefined && (
            <h3 className="font-bold text-3xl">{category.title}</h3>
          )}
          <ul className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {category.members?.map((member, index: number) => {
              return (
                <li
                  key={member._key ?? index}
                  className="flex flex-col gap-y-4 lg:gap-y-8 group"
                  onMouseEnter={() =>
                    setHoveredCodes(toMapCodes(member.countryCodes))
                  }
                  onMouseLeave={() => setHoveredCodes([])}
                >
                  <figure className="relative aspect-square">
                    <Image
                      className="object-cover grayscale group-hover:grayscale-0 duration-300"
                      fill
                      src={member.picture?.asset?.url}
                      alt="member-cover"
                    />
                  </figure>
                  <div className="flex flex-col gap-y-2">
                    <h5 className="font-bold text-2xl flex items-center gap-x-4">
                      {member.name}{' '}
                      <span className="text-xs flex flex-row">
                        {
                          <div className="flex flex-row gap-x-2">
                            {member?.countryCodes?.map((countryCode) => (
                              <Image
                                key={countryCode}
                                alt="country-code"
                                src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg`}
                                width={16}
                                height={16}
                              />
                            ))}
                          </div>
                        }
                      </span>
                    </h5>
                    <div className="flex flex-row items-center gap-x-2">
                      <p className="text-base">{member.role}</p>
                    </div>
                    <div className="text-sm">
                      <RichText value={member.bio as any} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {allCodes.length > 0 && (
        <div className="hidden lg:block w-full max-w-4xl mx-auto">
          <style>{`
            #world-map { width: 100%; height: auto; }
            #world-map path { fill: #e5e7eb; transition: fill 0.3s; }
            ${activeCodes
              .map(
                (code) =>
                  `#world-map #${code}, #world-map #${code} path { fill: ${activeColor}; }`,
              )
              .join('\n')}
          `}</style>
          <World />
        </div>
      )}
    </div>
  );
};

export default MembersSection;
